import React, { useEffect, useRef, useState, useCallback } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowRight, Check, GitBranch } from 'lucide-react';
import { Spinner } from '../design-system';
import { useOrganization } from '../contexts/OrganizationContext';
import { useAuthenticatedApi } from '../hooks/useAuthenticatedApi';
import { apiEndpoints } from '../utils/api';
import { ApplicationListResponse } from '../types/application';
import { FindingStatsResponse } from '../types/finding';
import PostHogService, { DemoEvent, OnboardingEvent, TrustEvent } from '../posthog';
import toast from '../design-system/toast';
import OnboardingProgress from '../components/onboarding/OnboardingProgress';
import { useEnabledProviders } from '../hooks/useEnabledProviders';
import { GitProvider, ProviderIcon, isGitProvider, joinProviderLabels, providerLabel } from '../config/providers';
import CodeCommitConnectForm from '../components/organization/CodeCommitConnectForm';
import {
  CodeCommitConnectPayload,
  CodeCommitRoleSetup,
  OrgCodeCommitStatus,
  codecommitConnectionDisplayName,
} from '../types/codecommit';

interface OrgGitHubStatus {
  connected: boolean;
  github_username?: string;
  avatar_url?: string;
}

interface OrgGitLabStatus {
  connected: boolean;
  gitlab_username?: string;
  avatar_url?: string;
}

interface OrgAzureDevOpsStatus {
  connected: boolean;
  ado_username?: string;
  ado_organization_url?: string;
}

interface DemoFixture {
  id: string;
  slug: string;
  name: string;
  description?: string;
  repository_full_name: string;
  default_branch: string;
  language?: string;
  frameworks?: string[];
}

interface DemoFixtureListResponse {
  fixtures: DemoFixture[];
}

interface DemoScanResponse {
  organization_id: string;
  application_id: string;
  batch_id: string;
}

// Providers this page has a connection card for. Enabled providers without a card
// are simply not offered here until their onboarding UI exists.
const ONBOARDING_CARD_PROVIDERS: readonly GitProvider[] = ['github', 'gitlab', 'azure_devops', 'codecommit'];

/** Credential-form providers that take over the page while their form is open. */
type ActiveForm = 'azure_devops' | 'codecommit' | null;

const OnboardingGitPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const {
    currentOrganization,
    organizations,
    isLoading: orgLoading,
    createOrganization,
    refreshOrganizations,
    switchOrganization,
  } = useOrganization();
  const api = useAuthenticatedApi();
  const enabledProviders = useEnabledProviders();
  const cardProviders = enabledProviders.filter((p) => ONBOARDING_CARD_PROVIDERS.includes(p));

  const [githubStatus, setGithubStatus] = useState<OrgGitHubStatus | null>(null);
  const [gitlabStatus, setGitlabStatus] = useState<OrgGitLabStatus | null>(null);
  const [adoStatus, setAdoStatus] = useState<OrgAzureDevOpsStatus | null>(null);
  const [codecommitStatus, setCodecommitStatus] = useState<OrgCodeCommitStatus | null>(null);
  const [demoFixtures, setDemoFixtures] = useState<DemoFixture[]>([]);
  const [demoActionableCount, setDemoActionableCount] = useState<number | null>(null);
  const [selectedDemoFixtureId, setSelectedDemoFixtureId] = useState('');
  const [demoLoading, setDemoLoading] = useState(true);
  const [startingDemo, setStartingDemo] = useState(false);
  const [loading, setLoading] = useState(true);
  const [connectingGitHub, setConnectingGitHub] = useState(false);
  const [connectingGitLab, setConnectingGitLab] = useState(false);
  const [activeForm, setActiveForm] = useState<ActiveForm>(null);
  const [adoForm, setAdoForm] = useState({ ado_organization: '', pat: '', pat_expires_at: '' });
  const [connectingAdo, setConnectingAdo] = useState(false);
  const [adoFormError, setAdoFormError] = useState<string | null>(null);

  const organizationId = currentOrganization?.id;
  const includeCodecommit = cardProviders.includes('codecommit');
  const hasTrackedViewed = useRef(false);
  const isDemoOrganization = !!currentOrganization?.is_demo;
  const hasCompletedDemoOnboarding = organizations.some((org) => org.is_demo);
  const demoOrganizationId = organizations.find((org) => org.is_demo)?.id;

  // Track onboarding step view once per mount (StrictMode-safe)
  useEffect(() => {
    if (hasTrackedViewed.current || orgLoading) return;
    hasTrackedViewed.current = true;
    PostHogService.track(OnboardingEvent.GIT_CONNECT_VIEWED, {
      organization_id: organizationId ?? null,
    });
  }, [orgLoading, organizationId]);

  useEffect(() => {
    let cancelled = false;
    const fetchDemoFixtures = async () => {
      if (hasCompletedDemoOnboarding) {
        setDemoFixtures([]);
        setSelectedDemoFixtureId('');
        setDemoLoading(false);
        return;
      }

      setDemoLoading(true);
      try {
        const response = await api.get<DemoFixtureListResponse>(apiEndpoints.onboarding.demoRepositories);
        if (cancelled) return;
        const fixtures = response.fixtures || [];
        setDemoFixtures(fixtures);
        setSelectedDemoFixtureId((current) => current || fixtures[0]?.id || '');
      } catch (e) {
        console.error('Failed to fetch demo repositories', e);
      } finally {
        if (!cancelled) setDemoLoading(false);
      }
    };
    fetchDemoFixtures();
    return () => {
      cancelled = true;
    };
  }, [api, hasCompletedDemoOnboarding]);

  // Actionable finding count from the demo scan, used to bridge the post-demo state
  // into connecting a real repository. The demo workspace holds exactly one application.
  useEffect(() => {
    if (!demoOrganizationId) {
      setDemoActionableCount(null);
      return;
    }

    let cancelled = false;
    const fetchDemoActionableCount = async () => {
      try {
        const applicationsResponse = await api.get<ApplicationListResponse>(
          apiEndpoints.applications.list(demoOrganizationId)
        );
        if (cancelled) return;
        const demoApplication = applicationsResponse.applications?.[0];
        if (!demoApplication) return;

        const stats = await api.get<FindingStatsResponse>(
          apiEndpoints.findings.applicationStats(demoApplication.id)
        );
        if (cancelled) return;
        // "Actionable" mirrors ACTIONABLE_FINDING_STATUSES on the backend (open +
        // needs_manual_review), so dismissed and already-fixed findings are excluded.
        // Derived by subtracting the non-actionable statuses from the total because the
        // stats endpoint omits needs_manual_review from by_status and so cannot be summed.
        const nonActionable =
          stats.by_status.false_positive + stats.by_status.ignored + stats.by_status.resolved;
        setDemoActionableCount(stats.total - nonActionable);
      } catch (e) {
        console.error('Failed to fetch demo scan actionable finding count', e);
      }
    };
    fetchDemoActionableCount();
    return () => {
      cancelled = true;
    };
  }, [api, demoOrganizationId]);

  const fetchStatuses = useCallback(async () => {
    if (!organizationId || isDemoOrganization) {
      setLoading(false);
      return;
    }
    try {
      const [ghRes, glRes, adoRes, ccRes] = await Promise.all([
        api.get<OrgGitHubStatus>(`/organizations/${organizationId}/github/status`).catch(() => null),
        api.get<OrgGitLabStatus>(`/organizations/${organizationId}/gitlab/status`).catch(() => null),
        api.get<OrgAzureDevOpsStatus>(apiEndpoints.orgAzureDevOps.status(organizationId)).catch(() => null),
        // Feature-gated: the endpoint 404s when CodeCommit is off, so only ask when it is offered.
        includeCodecommit
          ? api.get<OrgCodeCommitStatus>(apiEndpoints.orgCodeCommit.status(organizationId)).catch(() => null)
          : Promise.resolve<OrgCodeCommitStatus | null>(null),
      ]);
      setGithubStatus(ghRes);
      setGitlabStatus(glRes);
      setAdoStatus(adoRes);
      setCodecommitStatus(ccRes);
    } catch (e) {
      console.error('Failed to fetch git provider statuses', e);
    } finally {
      setLoading(false);
    }
  }, [api, organizationId, isDemoOrganization, includeCodecommit]);

  useEffect(() => {
    if (orgLoading) return;
    fetchStatuses();
  }, [orgLoading, organizationId, fetchStatuses]);

  // Handle OAuth callback query parameters
  useEffect(() => {
    const provider = searchParams.get('provider');
    const status = searchParams.get('status');
    const error = searchParams.get('error');

    // Check for connection success
    if (provider && status === 'connected') {
      const providerName = isGitProvider(provider) ? providerLabel(provider) : provider;
      toast.success(`${providerName} connected successfully!`);
      PostHogService.track(OnboardingEvent.GIT_CONNECTED, {
        provider,
        organization_id: organizationId,
      });
      searchParams.delete('provider');
      searchParams.delete('status');
      setSearchParams(searchParams, { replace: true });
      fetchStatuses();
      return;
    }

    // Check for errors
    if (error) {
      const providerName = isGitProvider(provider) ? providerLabel(provider) : null;

      if (error === 'oauth_state_expired' || error === 'oauth_state_invalid') {
        toast.error('Connection expired. Please try again.');
      } else if (error === 'oauth_invalid_response') {
        toast.error(`${providerName || 'Provider'} returned an invalid response. Please try again.`);
      } else if (error === 'oauth_invalid_user_info') {
        toast.error(`Failed to retrieve ${providerName || 'provider'} user information. Please try again.`);
      } else if (error === 'oauth_connection_failed' && providerName) {
        toast.error(`Failed to connect ${providerName}. Please try again.`);
      } else {
        toast.error('Connection failed. Please try again.');
      }
      PostHogService.track(OnboardingEvent.GIT_CONNECT_FAILED, {
        provider: provider ?? 'unknown',
        organization_id: organizationId,
        error_code: error,
      });
      searchParams.delete('provider');
      searchParams.delete('error');
      setSearchParams(searchParams, { replace: true });
    }
  }, [searchParams, setSearchParams, fetchStatuses, organizationId]);

  const ensureRealOrganization = async (): Promise<string> => {
    if (currentOrganization?.id && !currentOrganization.is_demo) {
      return currentOrganization.id;
    }
    const existingOrganization = organizations.find((org) => !org.is_demo);
    if (existingOrganization) {
      switchOrganization(existingOrganization.id);
      return existingOrganization.id;
    }
    const organization = await createOrganization('Default Org', { source: 'onboarding' });
    return organization.id;
  };

  const handleConnectGitHub = async () => {
    setConnectingGitHub(true);
    try {
      const orgId = await ensureRealOrganization();
      PostHogService.track(OnboardingEvent.GIT_CONNECT_CLICKED, {
        provider: 'github',
        organization_id: orgId,
      });
      const redirectTo = '/onboarding/application';
      const res = await api.get<{ authorization_url: string }>(
        `/organizations/${orgId}/github/connect?redirect_to=${encodeURIComponent(redirectTo)}`
      );
      window.location.href = res.authorization_url;
    } catch (e) {
      console.error('Failed to initiate org GitHub connection', e);
      PostHogService.track(OnboardingEvent.GIT_CONNECT_FAILED, {
        provider: 'github',
        organization_id: organizationId ?? null,
        error_code: 'network_error',
      });
      toast.error('Failed to connect to GitHub');
      setConnectingGitHub(false);
    }
  };

  const handleConnectGitLab = async () => {
    setConnectingGitLab(true);
    try {
      const orgId = await ensureRealOrganization();
      PostHogService.track(OnboardingEvent.GIT_CONNECT_CLICKED, {
        provider: 'gitlab',
        organization_id: orgId,
      });
      const redirectTo = '/onboarding/application';
      const res = await api.get<{ authorization_url: string }>(
        `/organizations/${orgId}/gitlab/connect?redirect_to=${encodeURIComponent(redirectTo)}`
      );
      window.location.href = res.authorization_url;
    } catch (e) {
      console.error('Failed to initiate org GitLab connection', e);
      PostHogService.track(OnboardingEvent.GIT_CONNECT_FAILED, {
        provider: 'gitlab',
        organization_id: organizationId ?? null,
        error_code: 'network_error',
      });
      toast.error('Failed to connect to GitLab');
      setConnectingGitLab(false);
    }
  };

  const handleConnectAzureDevOps = async () => {
    setAdoFormError(null);
    const org = adoForm.ado_organization.trim();
    const pat = adoForm.pat.trim();
    if (!org || !pat) {
      setAdoFormError('Organization name and PAT are required.');
      return;
    }
    if (adoForm.pat_expires_at) {
      const today = new Date().toISOString().split('T')[0];
      if (adoForm.pat_expires_at <= today) {
        setAdoFormError('Token expiration date must be a future date.');
        return;
      }
    }
    setConnectingAdo(true);
    try {
      const orgId = await ensureRealOrganization();
      PostHogService.track(OnboardingEvent.GIT_CONNECT_CLICKED, {
        provider: 'azure_devops',
        organization_id: orgId,
      });
      await api.post(apiEndpoints.orgAzureDevOps.connect(orgId), {
        ado_organization_url: `https://dev.azure.com/${org}`,
        pat,
        pat_expires_at: adoForm.pat_expires_at || null,
      });
      setAdoForm({ ado_organization: '', pat: '', pat_expires_at: '' });
      setActiveForm(null);
      await fetchStatuses();
      PostHogService.track(OnboardingEvent.GIT_CONNECTED, {
        provider: 'azure_devops',
        organization_id: orgId,
      });
      toast.success('Azure DevOps connected successfully!');
    } catch (e: any) {
      const reason = e?.data?.detail || 'Could not connect. Check your organization name and token.';
      PostHogService.track(OnboardingEvent.GIT_CONNECT_FAILED, {
        provider: 'azure_devops',
        organization_id: organizationId ?? null,
        error_code: reason,
      });
      setAdoFormError(reason);
    } finally {
      setConnectingAdo(false);
    }
  };

  // Onboarding may still be on the demo org, so resolve a real organization before
  // talking to the CodeCommit endpoints (role-setup is org-scoped, connect persists).
  const loadCodecommitRoleSetup = useCallback(async (): Promise<CodeCommitRoleSetup> => {
    const orgId = await ensureRealOrganization();
    return api.get<CodeCommitRoleSetup>(apiEndpoints.orgCodeCommit.roleSetup(orgId));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [api, currentOrganization?.id, organizations]);

  const handleConnectCodeCommit = async (payload: CodeCommitConnectPayload) => {
    let orgId: string | null = null;
    try {
      orgId = await ensureRealOrganization();
      PostHogService.track(OnboardingEvent.GIT_CONNECT_CLICKED, {
        provider: 'codecommit',
        organization_id: orgId,
      });
      await api.post(apiEndpoints.orgCodeCommit.connect(orgId), payload);
      setActiveForm(null);
      await fetchStatuses();
      PostHogService.track(OnboardingEvent.GIT_CONNECTED, {
        provider: 'codecommit',
        organization_id: orgId,
      });
      toast.success('AWS CodeCommit connected successfully!');
    } catch (e: any) {
      const reason = e?.data?.detail || e?.message || 'Could not connect. Check your region and credentials.';
      PostHogService.track(OnboardingEvent.GIT_CONNECT_FAILED, {
        provider: 'codecommit',
        organization_id: orgId ?? organizationId ?? null,
        error_code: reason,
      });
      // Re-throw so the form surfaces the message inline.
      throw e;
    }
  };

  const handleStartDemoScan = async () => {
    if (hasCompletedDemoOnboarding) {
      toast.error('You have already scanned a demo repository');
      return;
    }
    if (!selectedDemoFixtureId) {
      toast.error('Select a demo repository first');
      return;
    }
    const selectedFixture = demoFixtures.find((fixture) => fixture.id === selectedDemoFixtureId);
    PostHogService.track(DemoEvent.SCAN_START_CLICKED, {
      organization_id: organizationId ?? null,
      demo_fixture_id: selectedDemoFixtureId,
      demo_fixture_slug: selectedFixture?.slug,
      repository_full_name: selectedFixture?.repository_full_name,
      default_branch: selectedFixture?.default_branch,
      path: `${location.pathname}${location.search}`,
    });
    setStartingDemo(true);
    try {
      const response = await api.post<DemoScanResponse>(apiEndpoints.onboarding.demoScan, {
        demo_fixture_id: selectedDemoFixtureId,
      });
      await refreshOrganizations();
      navigate(`/repositories/${response.application_id}/scans?org=${response.organization_id}`, {
        replace: true,
        state: { newBatchId: response.batch_id },
      });
    } catch (e: any) {
      console.error('Failed to start demo scan', e);
      toast.error(e?.data?.detail || e?.message || 'Failed to start demo scan');
    } finally {
      setStartingDemo(false);
    }
  };

  const handleTrustLinkClicked = () => {
    PostHogService.track(TrustEvent.LINK_CLICKED, {
      surface: 'onboarding_connect',
    });
  };

  const handleContinue = () => {
    const connectedProviders: string[] = [];
    if (githubStatus?.connected) connectedProviders.push('github');
    if (gitlabStatus?.connected) connectedProviders.push('gitlab');
    if (adoStatus?.connected) connectedProviders.push('azure_devops');
    if (codecommitStatus?.connected) connectedProviders.push('codecommit');
    PostHogService.track(OnboardingEvent.GIT_CONNECT_CONTINUED, {
      organization_id: organizationId,
      connected_providers: connectedProviders,
    });
    navigate('/onboarding/application', { replace: true });
  };

  if (orgLoading || loading || demoLoading) {
    return (
      <div className="min-h-screen bg-ui-background-primary flex items-center justify-center">
        <Spinner size={32} />
      </div>
    );
  }

  const isGitHubConnected = githubStatus?.connected;
  const isGitLabConnected = gitlabStatus?.connected;
  const isAdoConnected = adoStatus?.connected;
  const codecommitConnections = codecommitStatus?.connections ?? [];
  const isCodecommitConnected = !!codecommitStatus?.connected && codecommitConnections.length > 0;
  const isAnyConnected = isGitHubConnected || isGitLabConnected || isAdoConnected || isCodecommitConnected;
  const providerList = joinProviderLabels(cardProviders);

  // Body of each provider's connection card (status / connect action).
  const renderProviderCardBody = (provider: GitProvider): React.ReactNode => {
    switch (provider) {
      case 'github':
        return (
          <>
            {isGitHubConnected ? (
              <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-3">
                <div className="flex items-center gap-2">
                  {githubStatus?.avatar_url && (
                    <img
                      src={githubStatus.avatar_url}
                      alt="GitHub avatar"
                      className="w-8 h-8 rounded-full border-2 border-green-300 dark:border-green-700"
                    />
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <Check className="w-4 h-4 text-green-600 dark:text-green-400" />
                      <span className="font-medium text-green-800 dark:text-green-200 text-sm">
                        Connected
                      </span>
                    </div>
                    {githubStatus?.github_username && (
                      <p className="text-xs text-green-700 dark:text-green-300 truncate">
                        @{githubStatus.github_username}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            ) : (
              <button
                onClick={handleConnectGitHub}
                disabled={connectingGitHub}
                className="w-full py-2.5 px-4 bg-brand-primary hover:bg-brand-primaryDark text-black
                           font-medium rounded-lg transition-colors flex items-center justify-center gap-2
                           disabled:opacity-50 disabled:cursor-not-allowed text-sm"
              >
                {connectingGitHub ? (
                  <>
                    <Spinner size={16} tone="current" />
                    Connecting...
                  </>
                ) : (
                  <>
                    <ProviderIcon provider="github" className="w-4 h-4" />
                    Connect
                  </>
                )}
              </button>
            )}
          </>
        );
      case 'gitlab':
        return (
          <>
            {isGitLabConnected ? (
              <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-3">
                <div className="flex items-center gap-2">
                  {gitlabStatus?.avatar_url && (
                    <img
                      src={gitlabStatus.avatar_url}
                      alt="GitLab avatar"
                      className="w-8 h-8 rounded-full border-2 border-green-300 dark:border-green-700"
                    />
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <Check className="w-4 h-4 text-green-600 dark:text-green-400" />
                      <span className="font-medium text-green-800 dark:text-green-200 text-sm">
                        Connected
                      </span>
                    </div>
                    {gitlabStatus?.gitlab_username && (
                      <p className="text-xs text-green-700 dark:text-green-300 truncate">
                        @{gitlabStatus.gitlab_username}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            ) : (
              <button
                onClick={handleConnectGitLab}
                disabled={connectingGitLab}
                className="w-full py-2.5 px-4 bg-brand-primary hover:bg-brand-primaryDark text-black
                           font-medium rounded-lg transition-colors flex items-center justify-center gap-2
                           disabled:opacity-50 disabled:cursor-not-allowed text-sm"
              >
                {connectingGitLab ? (
                  <>
                    <Spinner size={16} tone="current" />
                    Connecting...
                  </>
                ) : (
                  <>
                    <ProviderIcon provider="gitlab" className="w-4 h-4" />
                    Connect
                  </>
                )}
              </button>
            )}
          </>
        );
      case 'azure_devops':
        return (
          <>
            {isAdoConnected ? (
              <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-3">
                <div className="flex items-center gap-2">
                  <Check className="w-4 h-4 text-green-600 dark:text-green-400" />
                  <span className="font-medium text-green-800 dark:text-green-200 text-sm">Connected</span>
                </div>
                {adoStatus?.ado_username && (
                  <p className="text-xs text-green-700 dark:text-green-300 truncate">
                    @{adoStatus.ado_username}
                  </p>
                )}
              </div>
            ) : (
              <button
                onClick={() => setActiveForm('azure_devops')}
                className="w-full py-2.5 px-4 bg-brand-primary hover:bg-brand-primaryDark text-black
                           font-medium rounded-lg transition-colors flex items-center justify-center gap-2
                           text-sm"
              >
                <ProviderIcon provider="azure_devops" className="w-4 h-4" />
                Connect
              </button>
            )}
          </>
        );
      case 'codecommit':
        return (
          <>
            {isCodecommitConnected ? (
              <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-3">
                <div className="flex items-center gap-2">
                  <Check className="w-4 h-4 text-green-600 dark:text-green-400" />
                  <span className="font-medium text-green-800 dark:text-green-200 text-sm">Connected</span>
                </div>
                <p className="text-xs text-green-700 dark:text-green-300 truncate">
                  {codecommitConnections.length > 1
                    ? `${codecommitConnections.length} connections`
                    : codecommitConnectionDisplayName(codecommitConnections[0])}
                </p>
              </div>
            ) : (
              <button
                onClick={() => setActiveForm('codecommit')}
                className="w-full py-2.5 px-4 bg-brand-primary hover:bg-brand-primaryDark text-black
                           font-medium rounded-lg transition-colors flex items-center justify-center gap-2
                           text-sm"
              >
                <ProviderIcon provider="codecommit" className="w-4 h-4" />
                Connect
              </button>
            )}
          </>
        );
      default:
        return null;
    }
  };


  return (
    <div className="min-h-screen bg-ui-background-primary text-ui-text-primary flex flex-col">
      <OnboardingProgress currentStep={1} className="max-w-3xl" />

      {/* Main Content */}
      <main className="flex-1 flex items-center justify-center px-4 py-12">
        <div className="w-full max-w-3xl">
          {/* Header */}
          <div className="text-center mb-8">
            <h1 className="text-2xl font-bold mb-2">Choose How to Start</h1>
            <p className="text-ui-text-secondary">
              {hasCompletedDemoOnboarding
                ? `Connect ${providerList} to import your own repositories.`
                : isAnyConnected
                  ? 'Your organization is connected. You can scan a demo repository, connect additional providers, or proceed to the next step.'
                  : `Connect ${providerList} to import your own repositories — or scan a public demo repository first.`}
            </p>
          </div>

          {/* Azure DevOps Full-width Form */}
          {activeForm === 'azure_devops' && (
            <div className="mb-8">
              <button
                type="button"
                onClick={() => { setActiveForm(null); setAdoFormError(null); }}
                className="flex items-center gap-1.5 text-sm text-ui-text-tertiary hover:text-ui-text-primary transition-colors mb-4"
              >
                <ArrowRight className="w-4 h-4 rotate-180" />
                Back to providers
              </button>
              <div className="bg-ui-background-secondary rounded-lg p-6 border border-ui-border-primary">
                <div className="flex items-center justify-center gap-3 mb-5">
                  <div className="w-10 h-10 bg-ui-background-tertiary rounded-full flex items-center justify-center">
                    <ProviderIcon provider="azure_devops" className="w-6 h-6 text-ui-text-primary" />
                  </div>
                  <h3 className="text-lg font-semibold">Connect {providerLabel('azure_devops')}</h3>
                </div>

                <div className="max-w-lg mx-auto space-y-4">
                  <div className="rounded-lg bg-ui-background-tertiary px-4 py-3 text-sm text-ui-text-secondary space-y-1.5">
                    <p className="font-medium text-ui-text-secondary">How to create a Personal Access Token</p>
                    <ol className="list-decimal list-inside space-y-1">
                      <li>Click your <strong className="font-medium text-ui-text-secondary">profile avatar</strong> (top right) → <strong className="font-medium text-ui-text-secondary">Personal access tokens</strong></li>
                      <li>Click <strong className="font-medium text-ui-text-secondary">New Token</strong> and give it a name (e.g. "kolega")</li>
                      <li>Under <strong className="font-medium text-ui-text-secondary">Scopes</strong>, choose <strong className="font-medium text-ui-text-secondary">Custom defined</strong> and grant <strong className="font-medium text-ui-text-secondary">Read</strong> for <strong className="font-medium text-ui-text-secondary">Code</strong> and <strong className="font-medium text-ui-text-secondary">Project and Team</strong></li>
                      <li>Click <strong className="font-medium text-ui-text-secondary">Create</strong> and copy the token — it won't be shown again</li>
                    </ol>
                  </div>

                  {adoFormError && (
                    <p className="text-sm text-red-600 dark:text-red-400">{adoFormError}</p>
                  )}

                  <div className="grid gap-1">
                    <label className="text-xs font-medium text-ui-text-secondary">Organization ID</label>
                    <div className="flex items-center rounded border border-ui-border-primary bg-ui-background-secondary overflow-hidden">
                      <span className="px-3 py-2 text-sm text-ui-text-tertiary bg-ui-background-tertiary dark:bg-ui-background-tertiary border-r border-ui-border-primary whitespace-nowrap select-none">
                        dev.azure.com/
                      </span>
                      <input
                        type="text"
                        placeholder="your-org-id"
                        value={adoForm.ado_organization}
                        onChange={(e) => setAdoForm((f) => ({ ...f, ado_organization: e.target.value }))}
                        className="flex-1 px-3 py-2 bg-transparent text-ui-text-primary text-sm outline-none"
                      />
                    </div>
                  </div>

                  <div className="grid gap-1">
                    <label className="text-xs font-medium text-ui-text-secondary">Personal Access Token</label>
                    <input
                      type="password"
                      placeholder="Paste your PAT here"
                      value={adoForm.pat}
                      onChange={(e) => setAdoForm((f) => ({ ...f, pat: e.target.value }))}
                      className="w-full px-3 py-2 rounded border border-ui-border-primary bg-ui-background-secondary text-ui-text-primary text-sm"
                    />
                  </div>

                  <div className="grid gap-1">
                    <label className="text-xs font-medium text-ui-text-secondary">
                      Token expiration date <span className="font-normal text-ui-text-tertiary">(optional)</span>
                    </label>
                    <input
                      type="date"
                      title="PAT expiration date (optional)"
                      min={new Date(Date.now() + 86400000).toISOString().split('T')[0]}
                      value={adoForm.pat_expires_at}
                      onChange={(e) => setAdoForm((f) => ({ ...f, pat_expires_at: e.target.value }))}
                      className="w-full px-3 py-2 rounded border border-ui-border-primary bg-ui-background-secondary text-ui-text-primary text-sm [&::-webkit-calendar-picker-indicator]:dark:invert"
                    />
                  </div>

                  <button
                    type="button"
                    onClick={handleConnectAzureDevOps}
                    disabled={connectingAdo}
                    className="w-full py-2.5 px-4 bg-brand-primary hover:bg-brand-primaryDark text-black
                               font-medium rounded-lg transition-colors flex items-center justify-center gap-2
                               disabled:opacity-50 disabled:cursor-not-allowed text-sm"
                  >
                    {connectingAdo ? (
                      <>
                        <Spinner size={16} tone="current" />
                        Connecting...
                      </>
                    ) : (
                      'Connect'
                    )}
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* AWS CodeCommit Full-width Form */}
          {activeForm === 'codecommit' && (
            <div className="mb-8">
              <button
                type="button"
                onClick={() => setActiveForm(null)}
                className="flex items-center gap-1.5 text-sm text-ui-text-tertiary hover:text-ui-text-primary transition-colors mb-4"
              >
                <ArrowRight className="w-4 h-4 rotate-180" />
                Back to providers
              </button>
              <div className="bg-ui-background-secondary rounded-lg p-6 border border-ui-border-primary">
                <div className="flex items-center justify-center gap-3 mb-5">
                  <div className="w-10 h-10 bg-ui-background-tertiary rounded-full flex items-center justify-center">
                    <ProviderIcon provider="codecommit" className="w-6 h-6 text-ui-text-primary" />
                  </div>
                  <h3 className="text-lg font-semibold">Connect {providerLabel('codecommit')}</h3>
                </div>
                <CodeCommitConnectForm
                  layout="wide"
                  loadRoleSetup={loadCodecommitRoleSetup}
                  onSubmit={handleConnectCodeCommit}
                />
              </div>
            </div>
          )}

          {/* Post-demo bridge: ties the demo scan result to connecting a real repository */}
          {!activeForm && hasCompletedDemoOnboarding && !!demoActionableCount && (
            <div className="bg-ui-background-secondary rounded-lg p-6 mb-6 border border-ui-border-primary">
              <p className="text-sm text-ui-text-secondary">
                You found <span className="text-brand-primary">{demoActionableCount}</span> issues in the
                sample repo — connect yours to see what's in your code.
              </p>
            </div>
          )}

          {/* Connection Cards */}
          {!activeForm && <div className="grid sm:grid-cols-2 gap-6 mb-8">
            {cardProviders.map((provider) => (
              <div key={provider} className="bg-ui-background-secondary rounded-lg p-6 border border-ui-border-primary">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 bg-ui-background-tertiary rounded-full flex items-center justify-center">
                    <ProviderIcon provider={provider} className="w-6 h-6 text-ui-text-primary" />
                  </div>
                  <h3 className="text-lg font-semibold">{providerLabel(provider)}</h3>
                </div>
                {renderProviderCardBody(provider)}
              </div>
            ))}
          </div>}

          {!activeForm && !hasCompletedDemoOnboarding && (
            <div className="bg-ui-background-secondary rounded-lg p-6 mb-6 border border-ui-border-primary">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-ui-background-tertiary rounded-full flex items-center justify-center">
                  <GitBranch className="w-5 h-5 text-ui-text-primary" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold">Or try the sandbox first</h3>
                  <p className="text-sm text-ui-text-secondary">
                    Try Kolega with a public GitHub repository before connecting your own account.
                  </p>
                </div>
              </div>

              <div className="grid gap-3 md:grid-cols-[1fr_auto]">
                <select
                  value={selectedDemoFixtureId}
                  onChange={(event) => {
                    const fixtureId = event.target.value;
                    setSelectedDemoFixtureId(fixtureId);
                    const fixture = demoFixtures.find((candidate) => candidate.id === fixtureId);
                    PostHogService.track(DemoEvent.REPOSITORY_SELECTED, {
                      organization_id: organizationId ?? null,
                      demo_fixture_id: fixtureId,
                      demo_fixture_slug: fixture?.slug,
                      repository_full_name: fixture?.repository_full_name,
                      default_branch: fixture?.default_branch,
                      path: `${location.pathname}${location.search}`,
                    });
                  }}
                  disabled={!demoFixtures.length || startingDemo}
                  className="w-full px-3 py-2 rounded border border-ui-border-primary bg-ui-background-secondary text-ui-text-primary text-sm"
                >
                  {demoFixtures.length ? (
                    demoFixtures.map((fixture) => (
                      <option key={fixture.id} value={fixture.id}>
                        {fixture.name} ({fixture.repository_full_name})
                      </option>
                    ))
                  ) : (
                    <option>No demo repositories available</option>
                  )}
                </select>
                <button
                  type="button"
                  onClick={handleStartDemoScan}
                  disabled={!selectedDemoFixtureId || startingDemo}
                  className="py-2.5 px-4 bg-brand-primary hover:bg-brand-primaryDark text-black font-medium rounded-lg transition-colors flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed text-sm"
                >
                  {startingDemo ? (
                    <>
                      <Spinner size={16} tone="current" />
                      Starting...
                    </>
                  ) : (
                    <>
                      <GitBranch className="w-4 h-4" />
                      Scan Demo Repo
                    </>
                  )}
                </button>
              </div>
            </div>
          )}

          {/* Trust reassurance: a note under the options, not another action card */}
          {!activeForm && (
            <div className="bg-ui-background-secondary/50 rounded-lg px-6 py-4 mb-6 border border-brand-primary/20
                            flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between sm:gap-6">
              <p className="text-sm text-ui-text-secondary">
                <span className="font-semibold text-ui-text-primary">Your code stays yours.</span>{' '}
                Scanned in an isolated container and destroyed within minutes — nothing stored at rest.
              </p>
              <a
                href="https://kolega.dev/trust/"
                target="_blank"
                rel="noopener noreferrer"
                onClick={handleTrustLinkClicked}
                className="text-sm font-semibold text-brand-primary hover:text-brand-primaryDark
                           whitespace-nowrap transition-colors"
              >
                Learn more →
              </a>
            </div>
          )}

          {/* Actions */}
          {isAnyConnected && (
            <div className="flex flex-col gap-3">
              <button
                onClick={handleContinue}
                className="w-full py-3 px-4 bg-brand-primary hover:bg-brand-primaryDark
                           text-black font-medium rounded-lg transition-colors
                           flex items-center justify-center gap-2"
              >
                Continue
                <ArrowRight className="w-5 h-5" />
              </button>
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

export default OnboardingGitPage;
